import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { initializeFaceTecSDK, startVerification } from '../services/faceTecService';

export function MfaVerification() {
  const { isAuth0Authenticated, completeMfaVerification, user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [status, setStatus] = useState('initializing');
  
  useEffect(() => {
    // Make sure user is authenticated with Auth0 first
    if (!isAuth0Authenticated) {
      navigate('/', { replace: true });
      return;
    }
    
    const initializeFaceTec = async () => {
      try {
        setStatus('initializing');
        setLoading(true);
        
        // Wait for FaceTec SDK to be loaded (it should be loaded via script tag)
        const checkFaceTecLoaded = () => {
          return new Promise((resolve, reject) => {
            let attempts = 0;
            const maxAttempts = 50; // 5 seconds
            
            const checkInterval = setInterval(() => {
              attempts++;
              
              if (typeof window.FaceTecSDK !== 'undefined') {
                clearInterval(checkInterval);
                resolve();
              } else if (attempts >= maxAttempts) {
                clearInterval(checkInterval);
                reject(new Error('FaceTec SDK failed to load'));
              }
            }, 100);
          });
        };
        
        await checkFaceTecLoaded();
        
        // Initialize FaceTec SDK following their documentation pattern
        await initializeFaceTecSDK();
        
        setStatus('ready');
      } catch (error) {
        console.error('Failed to initialize FaceTec:', error);
        setError('Failed to initialize facial verification: ' + error.message);
        setStatus('error');
      } finally {
        setLoading(false);
      }
    };
    
    initializeFaceTec();
  }, [isAuth0Authenticated, navigate]);
  
  const startFaceTecVerification = async () => {
    try {
      setStatus('verifying');
      setLoading(true);
      setError(null);
      
      // Get user ID for verification from AuthContext user object
      const userId = user?.id;
      if (!userId) {
        setError('User ID not available for verification');
        setStatus('error');
        setLoading(false);
        return;
      }
      
      console.log('Starting verification for user:', userId);
      
      // Start the FaceTec verification (1:1 matching)
      startVerification(userId, (result) => {
        console.log('FaceTec verification result:', result);
        
        if (result.success) {
          // Complete MFA verification
          completeMfaVerification();
          
          // Redirect to dashboard
          navigate('/dashboard', { replace: true });
        } else {
          setError('Facial verification failed: ' + (result.error || 'Please try again.'));
          setStatus('error');
          setLoading(false);
        }
      });
    } catch (error) {
      console.error('Facial verification failed:', error);
      setError('Facial verification failed: ' + (error.message || 'Please try again.'));
      setStatus('error');
      setLoading(false);
    }
  };
  
  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-gray-100 p-4">
      <div className="w-full max-w-md bg-white rounded-lg shadow-md p-6">
        <h1 className="text-2xl font-bold text-center mb-6">Biometric Verification</h1>
        
        {status === 'initializing' && (
          <div className="text-center py-4">
            <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-blue-500 mx-auto mb-4"></div>
            <p>Initializing facial verification...</p>
          </div>
        )}
        
        {status === 'ready' && (
          <div className="text-center py-4">
            <p className="mb-6">
              Please complete facial verification to continue. Your face will be matched 
              against your enrolled biometric template using FaceTec's 3D verification.
            </p>
            <button
              onClick={startFaceTecVerification}
              className="px-6 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 font-medium"
              disabled={loading}
            >
              Start Facial Verification
            </button>
          </div>
        )}
        
        {status === 'verifying' && (
          <div className="text-center py-4">
            <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-blue-500 mx-auto mb-4"></div>
            <p>Verifying your identity with 1:1 biometric matching...</p>
          </div>
        )}
        
        {status === 'error' && (
          <div className="text-center py-4">
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
              <p className="text-sm">{error}</p>
            </div>
            <button
              onClick={startFaceTecVerification}
              className="px-6 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 font-medium"
              disabled={loading}
            >
              Try Again
            </button>
          </div>
        )}
      </div>
    </div>
  );
}